import { Action } from 'util/domain';
import { CommonUtilsService } from 'util/services';

export interface AsyncStatusOptions<TResponse, TResult> {
    isComplete?: AsyncOpIsCompleteCheck<TResponse>;
    interval?: number;
    resultTransform?: AsyncOpResultTransform<TResponse, TResult>;
}
/**
 * Signature for a function that can be specified to transform the HTTP response for an async job result
 * to a different type.
 */
export interface AsyncOpResultTransform<TResponse, TResult> {
	(response: ng.IHttpPromiseCallbackArg<TResponse>): TResult;
}

/**
 * Function signature for the function used to check if an async job is completed based on the job's
 * HTTP response.
 */
export interface AsyncOpIsCompleteCheck<TResponse> {
	(responseBody: TResponse | AsyncOpStatus, response: ng.IHttpPromiseCallbackArg<TResponse | AsyncOpStatus>): boolean;
}

/**
 * Standard Status response for API's async jobs.
 */
export interface AsyncOpStatusCheck {
	Status: number;
	Links: {
		Cancel: string;
	}
}

/**
 * Standard completed response for API's async jobs.
 */
export interface CompletedAsyncOp {
	IsComplete: boolean;
	Links: {
		SelfUri: string;
		ResultUri: string;
	}
}

/**
 * Describes an async request and what actions can be done.
 */
export class AsyncOpHandle<TResponse, TResult> {
	public cancelled:boolean = false;
	public onCancel:Action = angular.noop;
	public onComplete:Action = angular.noop;

	/**
	 * A unique identifier for the async request.
	 */
	public readonly id:string = this.utils.generateUid();
	/**
	 * Continuously pings the API for the status of the async request.
	 * The promise will resolve with the result once the async request completes.
	 */
	public readonly status:ng.IPromise<TResult> = this.getStatus();

	constructor(
		private $q:ng.IQService,
		private $http:ng.IHttpService,
		private $timeout:ng.ITimeoutService,
		private utils:CommonUtilsService,
		public location:string,
		private options:AsyncStatusOptions<TResponse, TResult> = {}
	){
	}
	
	/**
	 * Invoke to cance the async request.
	 */
	public cancel(){
		this.cancelled = true;
		if(this.onCancel){
			this.onCancel();
		}
        return this.$http.delete(this.location);
	}
	
	/**
     * Helper method that actually performs the operation of continuously pinging the API status endpoint until the operation is either
     * complete or it has been cancelled. For each status update, the method will check if the operation is complete or has been cancelled,
     * and if not call itself to get the status again.
     */
    private getStatus() {
		let interval = this.options.interval || 500;
        return this.$q((resolve, reject) => {
            this.$timeout(() => {
                if (this.cancelled) {
                    resolve();
				} 
				else {
                    this.$http.get(this.location)
                        .then(resolve)
                        .catch(reject);
                }
            }, interval);
        })
        .then((response:ng.IHttpPromiseCallbackArg<TResponse>) => {
            let result:ng.IPromise<TResponse|TResult> | TResponse| TResult;
            if (this.cancelled) {
				return;
			} 
			
			if (this.options.isComplete(response.data, response)) {
				result = typeof this.options.resultTransform === 'function' ? this.options.resultTransform(response) : response.data;
				if(this.onComplete) {
					this.onComplete();
				}
            } else {
                interval = this.backoffInterval(interval);
                result = this.getStatus();
            }

            return result;
        });
	}
	
	/**
     * Helper method that slowly increases the status interval. 
     */
    private backoffInterval(timeout:number) {
        let result = 0;
        if (timeout > 0) {
            result = timeout + (timeout * 0.05);
        }

        return result > 2000 ? 2000 : result;
    }
}


/**
 * Combines other async request status types to provide a single common
 * status type.
 */
export type AsyncOpStatus = AsyncOpStatusCheck & CompletedAsyncOp;

/**
 * Type guard that checks if the argument is a {@link AsyncOpStatusCheck} type
 */
export const isAsyncOpStatusCheck = (status: any): status is AsyncOpStatusCheck => angular.isDefined(status.Status);

/**
 * Type guard that checks if the argument is a {@link CompletedAsyncOp} type.
 */
export const isCompletedAsyncOp = (status: any): status is CompletedAsyncOp => angular.isDefined(status.IsComplete);

/**
 * Type guard that checks if any type is an AsyncOpHandle
 */
export const isAsyncOpHandle = <T, R = undefined>(obj):obj is AsyncOpHandle<T, R> => {
	return obj && obj instanceof AsyncOpHandle;
}


// WEBPACK FOOTER //
// ./src/util/domain/async-op.ts